import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getTemplateCount } from '../../utils/services/documentAPI';
import { getallFolder } from '../../utils/services/folderAPI';
import { TagSearch } from './TagSearch';
import CreateResource from './CreateResource';
import { AppStoreContext } from '../../context/appStoreContext';
import { Popover, Icon, message } from 'antd';
import {
  FileTextIconSVG,
  PanelRightCloseIcon,
  PanelLeftCloseIcon,
  TagIcon,
  FolderIconSVG,
  UsersIcon,
  GlobeIcon,
  ClockIconSVG,
  CircleXIcon,
  ChevronRightIcon,
  ChevronDownIconSVG,
} from '../../images/svgIcons';
import { NEW_TEMPLATE_SECTION } from './translationValues';
import {
  SidebarContainer,
  SidebarContent,
  ToggleButton,
  LogoSection,
  LogoContent,
  LogoGroup,
  LogoIcon,
  LogoTitle,
  LogoSubtitle,
  ViewSwitcherSection,
  SearchInputWrapper,
  StyledSearchInput,
  ClearButton,
  ViewGrid,
  ViewButton,
  NavigationSection,
  NavGroup,
  NavGroupHeader,
  NavGroupTitle,
  AddButton,
  NavItems,
  FolderButton,
  ChevronIcon,
  FolderIcon,
  FolderName,
  FolderCount,
  SubFolders,
  SidebarLoader,
  Empty,
  EmptyIcon,
} from './styles';

function RecursiveFolderTree({ folders, selectedFolder, onSelectFolder, level = 0 }) {
  return (
    <>
      {folders?.map(folder => (
        <FolderItem
          key={folder.id}
          id={folder.id}
          name={folder.name}
          icon={<FolderIconSVG />}
          count={folder.count || folder.elements?.length}
          children={folder.subFolder}
          selectedFolder={selectedFolder}
          onSelectFolder={onSelectFolder}
          level={level}
          isExpandable={folder.subFolder?.length > 0}
        />
      ))}
    </>
  );
}

function FolderItem({
  id,
  name,
  icon,
  count = null,
  children,
  selectedFolder,
  onSelectFolder,
  level = 0,
  isExpandable = false,
  clearAllFilters,
  status,
  qaStatus,
  setQaStatus,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isSelected = ['all', 'waiting', 'rejected'].includes(selectedFolder)
    ? selectedFolder.split('+')[0] === id.toString()
    : selectedFolder === `${id}+${name}`;

  const isQaStatus = status === 'waiting' || status === 'rejected';

  const handleFolderClick = () => {
    if (id === 'all') {
      clearAllFilters();
      return;
    }
    if (isQaStatus) {
      setQaStatus(prev => (prev === status ? null : status));
      return;
    }
    onSelectFolder(`${id}+${name}`);
    if (isExpandable) {
      setIsExpanded(prev => !prev);
    }
  };

  return (
    <div>
      <FolderButton
        onClick={handleFolderClick}
        level={level}
        selected={isQaStatus ? qaStatus === status : isSelected}
        status={status}
      >
        {isExpandable && (
          <ChevronIcon selected={isSelected}>
            {isExpanded ? <ChevronDownIconSVG /> : <ChevronRightIcon />}
          </ChevronIcon>
        )}
        <FolderIcon selected={isSelected} status={status}>
          {icon}
        </FolderIcon>
        <FolderName>{name}</FolderName>
        {count !== null && (
          <FolderCount selected={isSelected} status={status}>
            {count}
          </FolderCount>
        )}
      </FolderButton>

      {children && isExpanded && (
        <SubFolders>
          <RecursiveFolderTree
            folders={children}
            selectedFolder={selectedFolder}
            onSelectFolder={onSelectFolder}
            level={level + 1}
          />
        </SubFolders>
      )}
    </div>
  );
}

const keys = {
  categories: 'category',
  folders: 'folder',
  teams: 'resourceGroup',
  languages: 'language',
};

const Sidebar = ({
  organizationView,
  setOrganizationView,
  selectedView,
  collapsed,
  onToggleCollapse,
  setSearchQuery,
  selectedTags,
  setSelectedTags,
  selectedFolder,
  onSelectFolder,
  qaStatus,
  setQaStatus,
  clearAllFilters,
  allLanguages,
  searchInputRef,
}) => {
  const { t } = useTranslation(['NEW_TEMPLATE_SECTION']);
  const [isLoading, setIsLoading] = useState(false);
  const [templateCount, setTemplatesCount] = useState(null);

  const { checkUserAccessRoles } = useContext(AppStoreContext);

  const filterEmptyFolders = folders => {
    return folders.reduce((acc, folder) => {
      // Recursively filter subfolders
      const filteredSubFolders = folder.subFolders ? filterEmptyFolders(folder.subFolders) : [];

      const hasElements = folder?.count > 0;
      const hasNonEmptySubFolders = filteredSubFolders.length > 0;

      // Keep folder only if it has elements OR has non-empty subfolders
      if (hasElements || hasNonEmptySubFolders) {
        acc.push({
          ...folder,
          subFolder: filteredSubFolders,
        });
      }

      return acc;
    }, []);
  };

  const fetchTemplateCount = async () => {
    const resourceType =
      selectedView === 'documents'
        ? 'document'
        : selectedView === 'reusable_pages'
        ? 'flow'
        : 'block';

    setIsLoading(true);
    try {
      const response = await getTemplateCount(resourceType, keys[organizationView]);
      if ([3221, 3222, 3223].includes(response?.statusCode) && response?.data) {
        if(organizationView === 'folders') {
          setTemplatesCount({ ...templateCount, folder: filterEmptyFolders(response.data?.folder) });
        } else {
          setTemplatesCount(response.data);
        }
      } else {
        message.error(
          t(
            NEW_TEMPLATE_SECTION.FAILED_TO_LOAD_TEMPLATE_COUNT.key,
            NEW_TEMPLATE_SECTION.FAILED_TO_LOAD_TEMPLATE_COUNT.value,
          ),
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const prevSelectedView = useRef('documents');
  useEffect(() => {
    if (prevSelectedView.current !== selectedView) {
      if (organizationView !== 'categories') {
        setOrganizationView('categories');
      } else {
        fetchTemplateCount();
      }
      setTemplatesCount(null);
      prevSelectedView.current = selectedView;
    } else {
      fetchTemplateCount();
    }
  }, [selectedView, organizationView]);

  const [showClear, setShowClear] = useState(false);

  const handleSearchInputChange = e => {
    if (e.key === 'Enter') {
      setSearchQuery(e.target.value);
    }
  };

  const onInputChange = e => {
    setShowClear(e.target.value.length > 0);
  };

  const handleClearSearch = () => {
    if (searchInputRef?.current) {
      searchInputRef.current.value = '';
      setSearchQuery('');
      setShowClear(false);
      searchInputRef.current.focus();
    }
  };

  const titleMap = {
    categories: t(
      NEW_TEMPLATE_SECTION.ALL_CATEGORIES.key,
      NEW_TEMPLATE_SECTION.ALL_CATEGORIES.value,
    ),
    folders: t(NEW_TEMPLATE_SECTION.ALL_FOLDERS.key, NEW_TEMPLATE_SECTION.ALL_FOLDERS.value),
    teams: t(NEW_TEMPLATE_SECTION.ALL_TEAMS.key, NEW_TEMPLATE_SECTION.ALL_TEAMS.value),
    languages: t(NEW_TEMPLATE_SECTION.ALL_LANGUAGES.key, NEW_TEMPLATE_SECTION.ALL_LANGUAGES.value),
  };

  const languageMap = Object.fromEntries(allLanguages?.map(l => [l.code, l.name]) ?? []);

  const languages =
    templateCount?.language
      ?.map(a => ({
        id: a.name,
        name: languageMap[a.name] || a.name,
        count: a.count,
      }))
      ?.filter(b => b.id != null) ?? [];

  return (
    <SidebarContainer collapsed={collapsed}>
      <ToggleButton collapsed={collapsed} onClick={onToggleCollapse}>
        {collapsed ? <PanelRightCloseIcon /> : <PanelLeftCloseIcon />}
      </ToggleButton>

      <SidebarContent collapsed={collapsed}>
        {/* Logo */}
        <LogoSection>
          <LogoContent>
            <LogoGroup>
              <LogoIcon>
                <FileTextIconSVG />
              </LogoIcon>
              <div>
                <LogoTitle>
                  {t(NEW_TEMPLATE_SECTION.SEARCH.key, NEW_TEMPLATE_SECTION.SEARCH.value)}
                </LogoTitle>
                <LogoSubtitle>
                  {t(NEW_TEMPLATE_SECTION.STUDIO.key, NEW_TEMPLATE_SECTION.STUDIO.value)}
                </LogoSubtitle>
              </div>
            </LogoGroup>
          </LogoContent>
        </LogoSection>

        {/* View Switcher */}
        <ViewSwitcherSection>
          {/* Search by name */}
          <SearchInputWrapper>
            <StyledSearchInput
              ref={searchInputRef}
              placeholder={t(
                NEW_TEMPLATE_SECTION.SEARCH_BY_NAME_PLACEHOLDER.key,
                NEW_TEMPLATE_SECTION.SEARCH_BY_NAME_PLACEHOLDER.value,
              )}
              onKeyDown={handleSearchInputChange}
              onChange={onInputChange}
            />
            {showClear && (
              <ClearButton onClick={handleClearSearch}>
                <CircleXIcon size={16} />
              </ClearButton>
            )}
          </SearchInputWrapper>
          <ViewGrid>
            <ViewButton
              active={organizationView === 'categories'}
              onClick={() => setOrganizationView('categories')}
              disabled={isLoading}
            >
              <TagIcon />
              {t(NEW_TEMPLATE_SECTION.CATEGORIES.key, NEW_TEMPLATE_SECTION.CATEGORIES.value)}
            </ViewButton>
            <ViewButton
              active={organizationView === 'folders'}
              onClick={() => setOrganizationView('folders')}
              disabled={isLoading}
            >
              <FolderIconSVG />
              {t(NEW_TEMPLATE_SECTION.FOLDERS.key, NEW_TEMPLATE_SECTION.FOLDERS.value)}
            </ViewButton>
            <ViewButton
              active={organizationView === 'teams'}
              onClick={() => setOrganizationView('teams')}
              disabled={isLoading}
            >
              <UsersIcon />
              {t(NEW_TEMPLATE_SECTION.TEAMS.key, NEW_TEMPLATE_SECTION.TEAMS.value)}
            </ViewButton>
            <ViewButton
              active={organizationView === 'languages'}
              onClick={() => setOrganizationView('languages')}
              disabled={isLoading}
            >
              <GlobeIcon />
              {t(NEW_TEMPLATE_SECTION.LANGUAGES.key, NEW_TEMPLATE_SECTION.LANGUAGES.value)}
            </ViewButton>
          </ViewGrid>
        </ViewSwitcherSection>

        {/* Navigation */}
        <NavigationSection>
          {/* Quick Access */}
          <NavGroup>
            <NavGroupHeader>
              <NavGroupTitle>
                {t(NEW_TEMPLATE_SECTION.QUICK_ACCESS.key, NEW_TEMPLATE_SECTION.QUICK_ACCESS.value)}
              </NavGroupTitle>
            </NavGroupHeader>
            <NavItems>
              <FolderItem
                id="all"
                name={t(NEW_TEMPLATE_SECTION.ALL.key, NEW_TEMPLATE_SECTION.ALL.value)}
                icon={<FileTextIconSVG size={16} />}
                count={templateCount?.communicationCount || templateCount?.templateCount}
                selectedFolder={selectedFolder}
                onSelectFolder={onSelectFolder}
                status="all"
                clearAllFilters={clearAllFilters}
              />
            </NavItems>
          </NavGroup>

          {/* Document Status */}
          <NavGroup>
            <NavGroupHeader>
              <NavGroupTitle>
                {t(NEW_TEMPLATE_SECTION.STATUS.key, NEW_TEMPLATE_SECTION.STATUS.value)}
              </NavGroupTitle>
            </NavGroupHeader>
            <NavItems>
              <FolderItem
                id="waiting"
                name={t(
                  NEW_TEMPLATE_SECTION.WAITING_FOR_APPROVAL.key,
                  NEW_TEMPLATE_SECTION.WAITING_FOR_APPROVAL.value,
                )}
                icon={<ClockIconSVG />}
                count={templateCount?.waitingForApprovalCount}
                selectedFolder={selectedFolder}
                onSelectFolder={onSelectFolder}
                status="waiting"
                qaStatus={qaStatus}
                setQaStatus={setQaStatus}
              />

              <FolderItem
                id="rejected"
                name={t(
                  NEW_TEMPLATE_SECTION.NEEDS_CHANGES.key,
                  NEW_TEMPLATE_SECTION.NEEDS_CHANGES.value,
                )}
                icon={<CircleXIcon />}
                count={templateCount?.rejectedCounts}
                selectedFolder={selectedFolder}
                onSelectFolder={onSelectFolder}
                status="rejected"
                qaStatus={qaStatus}
                setQaStatus={setQaStatus}
              />
            </NavItems>
          </NavGroup>

          {/* Search by Tags */}
          <NavGroup>
            <NavGroupHeader>
              <NavGroupTitle>
                {t(
                  NEW_TEMPLATE_SECTION.SEARCH_BY_TAGS.key,
                  NEW_TEMPLATE_SECTION.SEARCH_BY_TAGS.value,
                )}
              </NavGroupTitle>
            </NavGroupHeader>
            <TagSearch selectedTags={selectedTags} onTagsChange={setSelectedTags} />
          </NavGroup>

          {/* Dynamic Content Based on View */}
          <NavGroup>
            <NavGroupHeader>
              <NavGroupTitle>{titleMap[organizationView]}</NavGroupTitle>
              {/* {(organizationView === 'categories' ||
                organizationView === 'folders' ||
                (organizationView === 'teams' && checkUserAccessRoles(['ADMIN']))) && (
                <Popover
                  trigger="click"
                  content={
                    <CreateResource
                      selectedView={selectedView}
                      organizationView={organizationView}
                    />
                  }
                >
                  <AddButton>
                    <Icon type="plus" size={14} />
                  </AddButton>
                </Popover>
              )} */}
            </NavGroupHeader>
            {isLoading ? (
              <SidebarLoader>
                <Icon type="loading" />
              </SidebarLoader>
            ) : (organizationView === 'languages' ? (
                languages.length > 0
              ) : (
                templateCount?.[keys[organizationView]]?.length > 0
              )) ? (
              <NavItems>
                <RecursiveFolderTree
                  folders={
                    organizationView === 'languages'
                      ? languages
                      : templateCount?.[keys[organizationView]] || []
                  }
                  selectedFolder={selectedFolder}
                  onSelectFolder={onSelectFolder}
                />
              </NavItems>
            ) : (
              <Empty>
                <EmptyIcon>
                  <Icon type="inbox" size={40} />
                </EmptyIcon>
                <p>
                  {t(
                    NEW_TEMPLATE_SECTION.NO_DATA_AVAILABLE.key,
                    NEW_TEMPLATE_SECTION.NO_DATA_AVAILABLE.value,
                  )}
                </p>
              </Empty>
            )}
          </NavGroup>
        </NavigationSection>
      </SidebarContent>
    </SidebarContainer>
  );
};

export default Sidebar;
